
import { useState, useEffect } from "react";
import { Subscription } from "@/components/subscriptions/types";
import { useSearchParams } from "react-router-dom";
import { useSubscriptionConfig } from "./subscriptions/useSubscriptionConfig";
import { usePersonalSubscriptions } from "./subscriptions/usePersonalSubscriptions";
import { useSchoolSubscriptions } from "./subscriptions/useSchoolSubscriptions";
import { useSubscriptionActions } from "./subscriptions/useSubscriptionActions";

export function useSubscriptions(schoolId?: string, isPublicView = false) {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [dataFetched, setDataFetched] = useState(false);

  const { infradigitalConfig } = useSubscriptionConfig();
  const { 
    subscriptions: personalSubscriptions, 
    loading: personalLoading, 
    fetchSubscriptions 
  } = usePersonalSubscriptions();
  
  const { 
    subscriptions: schoolSubscriptions, 
    loading: schoolLoading, 
    fetchSubscriptionsForSchool 
  } = useSchoolSubscriptions();
  
  const { 
    loading: actionLoading, 
    handleCancelSubscription, 
    handleAutoRenewalToggle 
  } = useSubscriptionActions();

  const forceRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    setDataFetched(false); // Reset data fetched flag to allow refetching
  };

  useEffect(() => {
    // Prevent infinite fetching by checking if data has already been fetched
    if (dataFetched) return;
    
    const fetchData = async () => {
      if (schoolId && isPublicView) {
        await fetchSubscriptionsForSchool(schoolId, token);
      } else {
        await fetchSubscriptions();
      }
      setDataFetched(true);
    };
    
    fetchData();
  }, [refreshTrigger, schoolId, isPublicView, token, fetchSubscriptions, fetchSubscriptionsForSchool, dataFetched]);

  // Add school_id to subscriptions for public view
  const subscriptionsWithSchoolId = (schoolId && isPublicView && schoolSubscriptions)
    ? schoolSubscriptions.map(sub => ({
        ...sub,
        school_id: schoolId
      }))
    : schoolId && isPublicView
      ? []
      : personalSubscriptions;

  // Handle subscription cancellation for public view
  const handlePublicCancelSubscription = async (subscriptionId: string) => {
    try {
      // Call the same function but pass the token for authentication
      const result = await handleCancelSubscription(subscriptionId, token);
      if (result) {
        // Refresh subscriptions list after cancellation
        setDataFetched(false); // Reset to allow refetching
        if (schoolId) {
          await fetchSubscriptionsForSchool(schoolId, token);
        }
      }
      return result;
    } catch (error) {
      console.error('Error cancelling subscription in public view:', error);
      return false;
    }
  };

  // Handle auto-renewal toggle for public view
  const handlePublicAutoRenewalToggle = async (subscriptionId: string, currentValue: boolean) => {
    try {
      // Call the function but pass the token for authentication
      await handleAutoRenewalToggle(subscriptionId, currentValue, token);
      // Refresh subscriptions list after toggling auto-renewal
      setDataFetched(false); // Reset to allow refetching
      if (schoolId) {
        await fetchSubscriptionsForSchool(schoolId, token);
      }
    } catch (error) {
      console.error('Error toggling auto-renewal in public view:', error);
      throw error;
    }
  };

  return {
    subscriptions: subscriptionsWithSchoolId,
    loading: schoolId && isPublicView ? schoolLoading : personalLoading || actionLoading,
    infradigitalConfig,
    handleCancelSubscription: isPublicView 
      ? handlePublicCancelSubscription 
      : async (subscriptionId: string) => {
          const result = await handleCancelSubscription(subscriptionId);
          if (result) forceRefresh();
          return result;
        },
    handleAutoRenewalToggle: isPublicView
      ? handlePublicAutoRenewalToggle
      : async (subscriptionId: string, currentValue: boolean) => {
          await handleAutoRenewalToggle(subscriptionId, currentValue);
          forceRefresh();
        },
    forceRefresh,
    fetchSubscriptionsForSchool
  };
}
